import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { searchUsers, discoverUsers, followUser, unfollowUser } from "@/data/requests/user-follow-requests";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent } from "@/components/ui/card";
import { Search, UserPlus, UserMinus, User } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { useDebounce } from "@/hooks/use-debounce"; // Assuming this hook exists, if not I'll implement it or use setTimeout

export function UserSearch() {
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, 500);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: searchResults, isLoading: isSearching } = useQuery({
    queryKey: ["users", "search", debouncedSearch],
    queryFn: () => searchUsers(debouncedSearch),
    enabled: debouncedSearch.length > 0,
  });

  const { data: discoveryResults, isLoading: isDiscovering } = useQuery({
    queryKey: ["users", "discover"],
    queryFn: discoverUsers,
    enabled: debouncedSearch.length === 0,
  });

  const users = debouncedSearch.length > 0 
    ? (searchResults?.data?.data || []) 
    : (discoveryResults?.data?.data || []);

  const followMutation = useMutation({
    mutationFn: followUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast({ title: "User followed!" });
    },
    onError: () => {
      toast({ title: "Failed to follow user", variant: "destructive" });
    }
  });

  const unfollowMutation = useMutation({
    mutationFn: unfollowUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast({ title: "User unfollowed" });
    },
    onError: () => {
      toast({ title: "Failed to unfollow user", variant: "destructive" });
    }
  });

  const handleFollow = (userId) => followMutation.mutate(userId);
  const handleUnfollow = (userId) => unfollowMutation.mutate(userId);

  const isLoading = debouncedSearch.length > 0 ? isSearching : isDiscovering;

  return (
    <div className="space-y-6">
      <div className="relative">
        <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search users by name or email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-8"
        />
      </div>

      {isLoading ? (
        <div className="text-center py-8 text-muted-foreground">Loading...</div>
      ) : users.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          {debouncedSearch ? "No users found." : "No suggestions available."}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {users.map((user) => (
            <Card key={user.id} className="overflow-hidden">
              <CardContent className="p-4 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 overflow-hidden">
                  <Avatar>
                    <AvatarImage src={user.avatar} />
                    <AvatarFallback>{user.name?.charAt(0).toUpperCase() || <User className="h-4 w-4" />}</AvatarFallback>
                  </Avatar>
                  <div className="truncate">
                    <p className="font-medium truncate">{user.name}</p>
                    <p className="text-xs text-muted-foreground truncate">@{user.username || "user"}</p>
                    {user.level && (
                      <p className="text-xs text-primary mt-0.5">Lvl {user.level}</p>
                    )}
                  </div>
                </div>
                
                {user.isFollowing ? (
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="shrink-0"
                    onClick={() => handleUnfollow(user.id)}
                    disabled={unfollowMutation.isPending}
                  >
                    <UserMinus className="h-4 w-4 mr-1" /> Unfollow
                  </Button>
                ) : (
                  <Button 
                    size="sm" 
                    className="shrink-0"
                    onClick={() => handleFollow(user.id)}
                    disabled={followMutation.isPending}
                  >
                    <UserPlus className="h-4 w-4 mr-1" /> Follow
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
